import React from "react";
import { SplitPanel } from "@ucl/library";
import { LoadingV2 } from "@ucl/library";
import { TaskExecutionLoader } from "./TaskExecutionLoader";
import { useTaskExecutionComponent } from "./useTaskExecutionComponent";
import { faComment, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { TaskExecutionOverview } from "./TaskExecutionOverview";

import { TaskExecutionModuleRegistry } from "../TaskExecutionModule/registry";
import { TaskExecutionModuleFormRegistry } from "../TaskExecutionModule/TaskExecutionModuleProps";
import { TaskExecutionActivityFeed, CommentPermissions } from "./TaskExecutionActivityFeed";
import { isNil } from "lodash";
import { BoltTaskCompactGrid } from "../TaskGrid/TaskCompactGrid";
import { BoltTaskGridProps } from "../TaskGrid/TaskGrid";
import { relatedTasksColumns } from "../TaskGrid/config/RelatedTasksColumns";

export interface TaskExecutionComponentProps {
  taskId: string;
  customRegistry?: TaskExecutionModuleRegistry;
  onClose?: () => void;
  onTaskComplete?: () => Promise<void>;
  apiBaseUrl?: string;
  getAuthRequestHeader?: () => Promise<Record<string, string> | undefined>;
  formRegistry?: TaskExecutionModuleFormRegistry;
  isTaskOverviewReadOnly?: boolean;
  leftPanelIsDocked?: boolean;
  rightPanelIsDocked?: boolean;
  relatedTasksGridProps?: BoltTaskGridProps;
  showRelatedTasks?: boolean;
  commentPermissions?: CommentPermissions;
}

export const TaskExecutionComponent: React.FC<TaskExecutionComponentProps> = (
  props
) => {
  const {
    task,
    isLoading,
    handleTaskComplete,
    handleWithdraw,
    handleReinstate,
    handleReassignTask,
  } = useTaskExecutionComponent(props);

  return (
    <div className="task-execution-component">
      {isLoading ? (
        <LoadingV2 height="200px" />
      ) : task ? (
        <div className="task-execution-content">
          <div className="task-execution-header">
            <h1>
              {task.taskNumber_AsString} - {task.taskTemplateName}
            </h1>
          </div>
          <SplitPanel
            className="task-execution-split-panel"
            leftPanel={{
              isDocked: isNil(props.leftPanelIsDocked)
                ? true
                : props.leftPanelIsDocked,
              items: [
                {
                  title: "Overview",
                  content: (
                    <TaskExecutionOverview
                      task={task}
                      isReadOnly={props.isTaskOverviewReadOnly || false}
                      apiBaseUrl={props.apiBaseUrl || ""}
                      getAuthRequestHeader={
                        props.getAuthRequestHeader ||
                        (() => Promise.resolve({}))
                      }
                      onTaskReassigned={handleReassignTask}
                    />
                  ),
                  icon: faInfoCircle,
                },
                ...(props.relatedTasksGridProps && props.showRelatedTasks
                  ? [
                      {
                        title: "Related Tasks",
                        content: (
                          <BoltTaskCompactGrid
                            {...props.relatedTasksGridProps}
                            boltTaskColumnsConfiguration={relatedTasksColumns}
                            onlyIncludeProvidedColumnsIfSpecified={true}
                            excludedTaskId={task.id}
                            showRelatedTasks={true}
                            commentPermissions={props.relatedTasksGridProps.commentPermissions}
                          />
                        ),
                        icon: faInfoCircle,
                      },
                    ]
                  : []),
              ],
            }}
            centerPanel={
              <TaskExecutionLoader
                key={`${task.id}-${task.taskStatus}`}
                taskDetail={task.detail}
                taskTemplateDescription={task.taskTemplateDescription || ""}
                taskStatus={task.taskStatus}
                taskExecutionType={task.taskExecutionType}
                customRegistry={props.customRegistry}
                taskExecutionTypeFields={task.taskExecutionTypeFields || {}}
                apiBaseUrl={props.apiBaseUrl}
                getAuthRequestHeader={props.getAuthRequestHeader}
                onTaskComplete={handleTaskComplete}
                onWithdraw={handleWithdraw}
                onReinstate={handleReinstate}
                formRegistry={props.formRegistry}
                entityId={task.entityId}
                entityType={task.entityType}
                parentEntityId={task.parentEntityId}
                parentEntityType={task.parentEntityType}
              />
            }
            rightPanel={{
              isDocked: isNil(props.rightPanelIsDocked)
                ? true
                : props.rightPanelIsDocked,
              items: [
                {
                  title: "Comments",
                  content: (
                    <TaskExecutionActivityFeed
                      taskId={task.id}
                      apiBaseUrl={props.apiBaseUrl || ""}
                      getAuthRequestHeader={props.getAuthRequestHeader}
                      commentPermissions={props.commentPermissions}
                    />
                  ),
                  icon: faComment,
                },
              ],
            }}
          />
        </div>
      ) : (
        <div>Task not found</div>
      )}
    </div>
  );
};
